import { useRef, useLayoutEffect, useCallback } from "react";

/**
 * This is an interim implementation of the `useEffectEvent` hook from React:
 * https://react.dev/learn/separating-events-from-effects#declaring-an-effect-event
 *
 * It's intended to be used to separate non-reactive logic (events) from reactive logic (effects).
 * There are 2 rules that must be followed to use the callback returned from this hook safely:
 *
 * 1. It should only be called from within a useEffect hook (never within the render cycle).
 * 2. It should never be passed to child components or other custom hooks.
 *
 * More details on the intended usage for this hook can be found here:
 * https://www.notion.so/faire/Migrating-Class-Components-Functional-Components-18684de1ae3a44ca99d0e24b8b52ab6f?pvs=4#faabf10fc0df475c89af1c0fcfa27abb
 *
 * @param eventHandler event handler that we don't want to be reactive.
 * @returns a memoized event handler that can be used in a `useEffect` hook.
 */
export function useEffectEvent<P extends Array<unknown>, R = void>(
  eventHandler: (...args: P) => R
): (...args: P) => R {
  const handlerRef = useRef<((...args: P) => R) | null>(null);

  // In the real implementation, this would run before layout effects
  useLayoutEffect(() => {
    handlerRef.current = eventHandler;
  });

  return useCallback((...args: P) => {
    const fn = handlerRef.current;
    if (!fn) {
      throw new Error("Effect event was called during render");
    }
    return fn(...args);
  }, []);
}
